import { useCallback, useEffect, useState } from "react";
import {
  ORDER_PROGRESS_STATUSES,
  orderProgressLabel,
  orderProgressStepIndex,
  orderStatusLabel,
  customerOrderStatusLabel,
  customerOrderStatusColorKey,
  canChangeOrderShippingAddress,
  type OrderPickupDetails,
} from "@gaole/shared";
import { Check } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, apiDownload } from "../lib/api";
import { rm, fmtDate } from "../lib/format";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Countdown } from "../components/Countdown";
import { PickupScheduleCallout } from "../components/pickup/PickupScheduleCallout";
import { Badge, Button, PageLoader, statusColor } from "../components/ui";
import { openPaymentGateway } from "../lib/payments";
import { OrderItemThumb } from "../components/storefront/OrderItemThumb";
import { ListingTitle } from "../components/ListingTitle";
import { sortPaymentMethods } from "../lib/paymentMethods";
import { BankInTransferDetails } from "../components/payments/BankInTransferDetails";
import { BankInReceiptUploadForm } from "../components/payments/BankInReceiptUploadForm";
import { BankInReceiptViewModal, type BankReceiptPreview } from "../components/payments/BankInReceiptViewModal";
import { PurchaseReviewForm } from "../components/reviews/PurchaseReviewForm";
import { StarRating } from "../components/reviews/StarRating";
import { MarketplaceOrderProgress } from "../components/marketplace/MarketplaceOrderProgress";
import {
  MarketplaceOrderFulfilment,
  type SellerCollectDetails,
} from "../components/marketplace/MarketplaceOrderFulfilment";

const SHIPPING_PENDING_KINDS = new Set(["RAFFLE_WIN", "AUCTION_WIN", "VAULT_SHIPMENT"]);

function orderNeedsShipping(kind: string): boolean {
  return SHIPPING_PENDING_KINDS.has(kind);
}

interface ShippingAddress {
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  postcode: string;
  city: string;
  state: string;
  country: string;
}

interface OrderDetailDto {
  id: string;
  orderNo: string;
  kind: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  shippingFee: number;
  total: number;
  creditUsed: number;
  fulfilmentMethod?: string;
  shippingAddress: ShippingAddress | null;
  pickupDetails?: OrderPickupDetails | null;
  paymentDeadline: string | null;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  trackingNo: string | null;
  courier: string | null;
  sellerFulfilmentStatus?: string | null;
  sellerCollectDetails?: SellerCollectDetails | null;
  sellerShopName?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  buyerConfirmedAt?: string | null;
  items: {
    id: string;
    productId: string;
    marketplaceListingId?: string | null;
    name: string;
    price: number;
    discount: number;
    quantity: number;
    imageUrl?: string | null;
    imageBackgroundColor?: string | null;
    canReview?: boolean;
    review?: {
      id: string;
      rating: number;
      comment: string | null;
      images: string[];
      createdAt: string;
    } | null;
  }[];
  receipts: BankReceiptPreview[];
}

const STEPS = ORDER_PROGRESS_STATUSES;

function pickupDisplayStepIndex(status: string): number {
  const map: Record<string, number> = {
    PENDING_PAYMENT: 0,
    PAID: 1,
    PICKED: 2,
    PACKED: 3,
    SHIPPED: 3,
    DELIVERED: 4,
  };
  return map[status] ?? -1;
}

export function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { config, moduleEnabled } = useAuth();
  const [paying, setPaying] = useState(false);
  const [receiptPreviewIndex, setReceiptPreviewIndex] = useState<number | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api<{ order: OrderDetailDto }>(`/orders/mine/${id}`),
  });

  const order = data?.order;

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["order", id] });
  }, [queryClient, id]);

  useEffect(() => {
    if (!order) return;
    const redirect =
      order.status === "PENDING_PAYMENT" &&
      orderNeedsShipping(order.kind) &&
      !order.shippingAddress &&
      !order.pickupDetails;
    if (redirect) navigate(`/checkout/order/${order.id}`, { replace: true });
  }, [order, navigate]);

  useEffect(() => {
    if (!order) return;
    if (window.location.hash === "#bank-in-payment") {
      document.getElementById("bank-in-payment")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [order?.id]);

  if (isPending && !order) return <PageLoader />;
  if (!order) return null;
  const currentOrder = order;

  const isPickup = currentOrder.fulfilmentMethod === "PICKUP";
  const pickupReady =
    isPickup &&
    order.kind === "MARKETPLACE" &&
    order.sellerFulfilmentStatus === "READY_FOR_COLLECTION";
  const progressSteps = isPickup ? STEPS.filter((s) => s !== "SHIPPED") : STEPS;
  const progressLabels = progressSteps.map((s) => orderProgressLabel(s, currentOrder.fulfilmentMethod));
  const isUnpaid = currentOrder.status === "PENDING_PAYMENT";
  const pendingReceipts = order.receipts.filter((r) => r.status === "PENDING");
  const pendingReceipt = pendingReceipts[0];
  const rejectedReceipt = order.receipts.find((r) => r.status === "REJECTED");
  const uploadedReceipts = order.receipts.filter((r) => r.fileUrl);
  const needsFulfilmentBeforePay =
    isUnpaid &&
    orderNeedsShipping(currentOrder.kind) &&
    !currentOrder.shippingAddress &&
    !currentOrder.pickupDetails;
  const canChangeShippingAddress =
    canChangeOrderShippingAddress(currentOrder.status, pendingReceipt?.status ?? null) &&
    orderNeedsShipping(currentOrder.kind) &&
    Boolean(currentOrder.shippingAddress);
  const paidWithCredits = currentOrder.creditUsed > 0;
  const stepIndex = isPickup
    ? pickupDisplayStepIndex(currentOrder.status)
    : orderProgressStepIndex(currentOrder.status);
  const documentsAvailable = Boolean(order.paidAt) || (!isUnpaid && stepIndex >= 1);

  const payMethods = sortPaymentMethods([
    ...(config?.creditsEnabled && order.kind !== "CREDIT_TOPUP" ? ["CREDITS"] : []),
    ...(config?.paymentMethods.stripe ? ["STRIPE"] : []),
    ...(config?.paymentMethods.gkash ? ["GKASH"] : []),
    ...(config?.paymentMethods.bankIn ? ["BANK_IN"] : []),
  ]).filter((m) => m !== "BANK_IN");

  async function payNow(method: string) {
    setPaying(true);
    try {
      const res = await api<{ status: string; redirectUrl?: string; form?: { url: string; fields: Record<string, string> } }>(
        `/checkout/pay/${currentOrder.id}`,
        { body: { method } },
      );
      if (openPaymentGateway(res)) return;
      if (res.status === "REDIRECT" || res.status === "GKASH_FORM") {
        toast("error", "Couldn't open the payment gateway — check payment settings and try again.");
        return;
      }
      if (res.status === "PAID") {
        toast("success", "Paid!");
        await queryClient.invalidateQueries({ queryKey: ["wallet"] });
      }
      await invalidate();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Payment failed");
    } finally {
      setPaying(false);
    }
  }

  async function cancelOrder() {
    if (!window.confirm("Cancel this order?")) return;
    try {
      await api(`/orders/mine/${currentOrder.id}/cancel`, { body: {} });
      toast("info", "Order cancelled");
      await invalidate();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't cancel");
    }
  }

  const showBankIn = isUnpaid && config?.paymentMethods.bankIn;
  const reviewVisible = (item: (typeof currentOrder.items)[number]) =>
    Boolean(item.review) && (Boolean(item.marketplaceListingId) || Boolean(config?.shopReviewsEnabled));

  return (
    <div className="space-y-4 px-4 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-mono text-sm font-bold">{order.orderNo}</h1>
          <p className="text-[11px] text-white/45">{fmtDate(order.createdAt)}</p>
        </div>
        <Badge color={statusColor(customerOrderStatusColorKey(order.status, pendingReceipt ? "PENDING" : null))}>
          {customerOrderStatusLabel(order.status, pendingReceipt ? "PENDING" : null)}
        </Badge>
      </div>

      {order.pickupDetails && (
        <PickupScheduleCallout
          details={order.pickupDetails}
          readyForPickup={pickupReady}
        />
      )}

      {order.fulfilmentMethod === "VAULT" && (
        <div className="rounded-2xl border border-brand-pink/25 bg-brand-pink/[0.06] p-3.5 text-xs text-white/70">
          <p className="font-semibold text-white">{isUnpaid ? "Will be stored in your Vault" : "Stored in your Vault"}</p>
          <p className="mt-1">
            {isUnpaid
              ? "Once paid, these items go straight to your Vault — no shipping today."
              : "Request delivery or pickup any time from Inventory."}
          </p>
          {!isUnpaid && (
            <Link to="/inventory" className="mt-2 inline-block font-semibold text-brand-pink underline">
              Open Inventory
            </Link>
          )}
        </div>
      )}

      {/* Payment deadline — hidden once receipt is uploaded and awaiting verification */}
      {isUnpaid && order.paymentDeadline && !pendingReceipt && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-3.5 text-center">
          <p className="text-[11px] text-amber-300">
            {order.kind === "RAFFLE_WIN" || order.kind === "AUCTION_WIN"
              ? "Pay before the deadline or your win is forfeited"
              : "Complete payment before the order expires"}
          </p>
          <div className="mt-1 flex justify-center">
            <Countdown target={order.paymentDeadline} onDone={invalidate} />
          </div>
        </div>
      )}

      {isUnpaid && pendingReceipt && (
        <div className="rounded-2xl border border-blue-400/30 bg-blue-500/10 p-3.5 text-center">
          <p className="text-[11px] text-blue-300">
            Your receipt is with our team for verification. This order will not expire while we review it.
          </p>
        </div>
      )}

      {order.kind === "MARKETPLACE" && order.status === "PAID" && order.sellerFulfilmentStatus && (
        <>
          <MarketplaceOrderProgress
            fulfilmentMethod={order.fulfilmentMethod}
            sellerFulfilmentStatus={order.sellerFulfilmentStatus}
            buyerConfirmed={Boolean(order.buyerConfirmedAt) || order.sellerFulfilmentStatus === "BUYER_CONFIRMED"}
          />
        </>
      )}

      {order.kind === "MARKETPLACE" && (order.status === "PAID" || order.status === "CANCELLED") && order.sellerFulfilmentStatus && (
        <MarketplaceOrderFulfilment
          fulfilmentMethod={order.fulfilmentMethod}
          sellerFulfilmentStatus={order.status === "CANCELLED" ? "CANCELLED" : order.sellerFulfilmentStatus}
          trackingNo={order.trackingNo}
          courier={order.courier}
          sellerCollectDetails={order.sellerCollectDetails}
          cancelReason={order.cancelReason}
          sellerShopName={order.sellerShopName}
        />
      )}

      {order.kind === "MARKETPLACE" && order.status === "PAID" && order.sellerFulfilmentStatus && ["SHIPPED", "READY_FOR_COLLECTION", "COMPLETED"].includes(order.sellerFulfilmentStatus) && (
        <div className="rounded-2xl border border-white/5 bg-surface p-4">
          <p className="text-xs text-muted">Received your item? Confirm so the seller gets paid.</p>
          <Button
            variant="primary"
            className="mt-3"
            onClick={async () => {
              try {
                await api(`/marketplace/orders/${order.id}/confirm-received`, { method: "POST", body: {} });
                toast("success", "Order confirmed");
                invalidate();
              } catch (e) {
                toast("error", e instanceof ApiError ? e.message : "Could not confirm");
              }
            }}
          >
            Confirm received
          </Button>
        </div>
      )}

      {stepIndex >= 0 && order.kind !== "MARKETPLACE" && order.fulfilmentMethod !== "VAULT" && (
        <div className="rounded-2xl border border-white/5 bg-surface p-4">
          <div className="flex items-center">
            {progressSteps.map((step, i) => (
              <div key={step} className="flex flex-1 items-center last:flex-none">
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${
                    i <= stepIndex ? "bg-gradient-brand text-white" : "bg-white/10 text-white/40"
                  }`}
                >
                  {i < stepIndex ? <Check size={10} strokeWidth={3} /> : i + 1}
                </div>
                {i < progressSteps.length - 1 && (
                  <div className={`h-0.5 flex-1 ${i < stepIndex ? "bg-brand-pink" : "bg-white/10"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[8px] uppercase tracking-wide text-white/40">
            {progressLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          {!isPickup && order.trackingNo && (
            <div className="mt-3 rounded-xl bg-white/5 p-2.5 text-center">
              <p className="text-[10px] text-white/50">{order.courier ?? "Courier"} tracking</p>
              <a
                href={`https://easyparcel.com/my/easytrack/?tracking=${encodeURIComponent(order.trackingNo)}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs font-semibold text-brand-pink underline"
              >
                {order.trackingNo}
              </a>
            </div>
          )}
        </div>
      )}

      {documentsAvailable && (moduleEnabled("documents") || moduleEnabled("invoicing")) && (
        <div className="flex flex-wrap gap-2 rounded-2xl border border-white/5 bg-surface p-4">
          <p className="w-full font-display text-xs font-semibold">Order documents</p>
          {moduleEnabled("invoicing") && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() =>
                void apiDownload(`/orders/mine/${order.id}/documents/INVOICE`, `invoice-${order.orderNo}.pdf`).catch(
                  (err) => toast("error", err instanceof ApiError ? err.message : "Download failed"),
                )
              }
            >
              Tax invoice
            </Button>
          )}
          {moduleEnabled("documents") && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() =>
                void apiDownload(`/orders/mine/${order.id}/documents/RECEIPT`, `receipt-${order.orderNo}.pdf`).catch(
                  (err) => toast("error", err instanceof ApiError ? err.message : "Download failed"),
                )
              }
            >
              Payment receipt
            </Button>
          )}
        </div>
      )}

      {/* Pay now — online methods below bank transfer */}
      {needsFulfilmentBeforePay && (
        <div className="rounded-2xl border border-brand-pink/30 bg-brand-pink/10 p-4 text-center">
          <p className="text-xs text-white/70">Choose delivery or store pickup to complete payment.</p>
          <Link to={`/checkout/order/${order.id}`} className="mt-3 inline-block">
            <Button className="w-full">Continue to checkout</Button>
          </Link>
        </div>
      )}

      {/* Bank-in flow — shown first for manual payment */}
      {showBankIn && !needsFulfilmentBeforePay && (
        <div id="bank-in-payment" className="space-y-3 rounded-2xl border border-white/5 bg-surface p-4">
          <p className="font-display text-xs font-semibold">Manual bank transfer</p>
          <BankInTransferDetails
            instructions={config?.bankInInstructions}
            bank={config?.bankInBank}
            accountNumber={config?.bankInAccountNumber}
            accountName={config?.bankInAccountName}
            qrImageUrl={config?.bankInQrImageUrl}
          />
          <p className="text-xs">
            Amount: <span className="font-semibold text-brand-pink">{rm(order.total)}</span> · Reference:{" "}
            <span className="font-mono font-semibold">{order.orderNo}</span>
          </p>

          {pendingReceipt ? (
            <div className="space-y-2">
              <p className="rounded-xl bg-blue-500/10 px-3 py-2 text-center text-[11px] text-blue-300">
                {pendingReceipts.length === 1
                  ? `Receipt uploaded ${fmtDate(pendingReceipt.createdAt)} — pending verification`
                  : `${pendingReceipts.length} receipts uploaded ${fmtDate(pendingReceipt.createdAt)} — pending verification`}
              </p>
              {uploadedReceipts.length > 0 && (
                <div className="space-y-2">
                  {uploadedReceipts.map((receipt, index) => (
                    <button
                      key={receipt.id}
                      type="button"
                      onClick={() => setReceiptPreviewIndex(index)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-left transition-colors hover:border-brand-pink/30 hover:bg-white/[0.07] active:bg-white/10"
                    >
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-white">
                          View receipt{uploadedReceipts.length > 1 ? ` ${index + 1}` : ""}
                        </span>
                        <span className="block truncate text-[10px] text-white/45">
                          {fmtDate(receipt.createdAt)}
                          {receipt.status === "REJECTED" ? " · Rejected" : receipt.status === "PENDING" ? " · Pending" : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-brand-pink">Tap to view</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {uploadedReceipts.length > 0 && (
                <div className="space-y-2">
                  {uploadedReceipts.map((receipt, index) => (
                    <button
                      key={receipt.id}
                      type="button"
                      onClick={() => setReceiptPreviewIndex(index)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-left transition-colors hover:border-brand-pink/30 hover:bg-white/[0.07] active:bg-white/10"
                    >
                      <span className="min-w-0">
                        <span className="block text-xs font-semibold text-white">
                          View receipt{uploadedReceipts.length > 1 ? ` ${index + 1}` : ""}
                        </span>
                        <span className="block truncate text-[10px] text-white/45">
                          {fmtDate(receipt.createdAt)}
                          {receipt.status === "REJECTED" ? " · Rejected" : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-brand-pink">Tap to view</span>
                    </button>
                  ))}
                </div>
              )}
              <BankInReceiptUploadForm
                orderId={currentOrder.id}
                orderTotal={currentOrder.total}
                hasRejectedReceipt={Boolean(rejectedReceipt)}
                rejectReason={rejectedReceipt?.rejectReason}
                onUploaded={invalidate}
              />
            </div>
          )}
        </div>
      )}

      {isUnpaid && !pendingReceipt && !needsFulfilmentBeforePay && payMethods.length > 0 && (
        <div className="space-y-2 rounded-2xl border border-white/5 bg-surface p-4">
          <p className="font-display text-xs font-semibold">Other payment methods · {rm(order.total)}</p>
          {payMethods.map((m) => (
            <Button key={m} variant="outline" className="w-full" disabled={paying} onClick={() => payNow(m)}>
              Pay with {m === "GKASH" ? "Gkash / FPX" : m === "CREDITS" ? "credits" : "card (Stripe)"}
            </Button>
          ))}
        </div>
      )}

      {/* Items — moved after payment blocks; thumbnails in list below */}
      <section className="space-y-2">
        <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Items</h2>
        {order.items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-surface p-3">
            <OrderItemThumb
              src={item.imageUrl}
              alt={item.name}
              backgroundColor={item.imageBackgroundColor}
            />
            <div className="min-w-0 flex-1">
              <ListingTitle className="font-semibold text-white">{item.name}</ListingTitle>
              <p className="text-[10px] text-white/45">
                {rm(item.price)} × {item.quantity}
                {item.discount > 0 && <span className="text-emerald-400"> · −{rm(item.discount)}</span>}
              </p>
            </div>
            <span className="text-xs font-semibold">{rm(item.price * item.quantity - item.discount)}</span>
          </div>
        ))}
      </section>

      {order.items.some((i) => i.canReview || reviewVisible(i)) && (
        <section className="space-y-3">
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Product reviews
          </h2>
          {order.items.map((item) => {
            if (!item.canReview && !reviewVisible(item)) return null;
            if (item.review) {
              return (
                <div key={item.id} className="rounded-2xl border border-white/5 bg-surface p-4">
                  <p className="text-xs font-semibold text-white">{item.name}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <StarRating value={item.review.rating} readOnly size={14} />
                    <span className="text-[10px] text-white/40">{fmtDate(item.review.createdAt)}</span>
                  </div>
                  {item.review.comment ? (
                    <p className="mt-2 text-xs leading-relaxed text-white/75">{item.review.comment}</p>
                  ) : null}
                  {item.review.images.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.review.images.map((url) => (
                        <a
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="block h-14 w-14 overflow-hidden rounded-lg border border-white/10"
                        >
                          <img src={url} alt="" className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <PurchaseReviewForm
                key={item.id}
                orderItemId={item.id}
                productName={item.name}
                onSubmitted={invalidate}
              />
            );
          })}
        </section>
      )}

      {/* Totals */}
      <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
        <div className="flex justify-between text-white/60">
          <span>Subtotal</span>
          <span>{rm(order.subtotal)}</span>
        </div>
        {order.discountTotal > 0 && (
          <div className="mt-1 flex justify-between text-emerald-400">
            <span>Discounts</span>
            <span>−{rm(order.discountTotal)}</span>
          </div>
        )}
        {(order.shippingFee > 0 || isPickup) && (
          <div className="mt-1 flex justify-between text-white/60">
            <span>Shipping</span>
            <span>{isPickup || order.shippingFee === 0 ? "FREE (Pickup)" : rm(order.shippingFee)}</span>
          </div>
        )}
        <div className="mt-2 flex justify-between border-t border-white/10 pt-2 font-display text-sm font-bold">
          <span>Total</span>
          <span className="text-brand-pink">{paidWithCredits ? `${order.creditUsed} cr` : rm(order.total)}</span>
        </div>
      </section>

      {/* Address */}
      {order.pickupDetails && (order.pickupDetails.phone || order.pickupDetails.instructions || order.pickupDetails.wazeUrl || order.pickupDetails.googleMapsUrl) && (
        <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
          <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
            Pickup details
          </p>
          {order.pickupDetails.phone && (
            <p className="mt-2 text-white/60">Contact: {order.pickupDetails.phone}</p>
          )}
          {order.pickupDetails.instructions && (
            <p className="mt-1 text-white/50">{order.pickupDetails.instructions}</p>
          )}
          {(order.pickupDetails.wazeUrl || order.pickupDetails.googleMapsUrl) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {order.pickupDetails.wazeUrl && (
                <a
                  href={order.pickupDetails.wazeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 hover:border-brand-pink/40 hover:text-brand-pink"
                >
                  Waze
                </a>
              )}
              {order.pickupDetails.googleMapsUrl && (
                <a
                  href={order.pickupDetails.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/80 hover:border-brand-pink/40 hover:text-brand-pink"
                >
                  Google Maps
                </a>
              )}
            </div>
          )}
        </section>
      )}

      {order.shippingAddress && (
        <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
              Ship to
            </p>
            {canChangeShippingAddress && (
              <Link to={`/checkout/order/${order.id}?changeAddress=1`} className="text-[10px] font-medium text-brand-pink">
                Change address
              </Link>
            )}
          </div>
          <p className="text-[10px] text-white/40">Saved on this order when it was created — not your address book.</p>
          <p className="mt-2 font-semibold">
            {order.shippingAddress.name}{" "}
            <span className="font-normal text-white/50">· {order.shippingAddress.phone}</span>
          </p>
          <p className="mt-0.5 text-white/60">
            {order.shippingAddress.address1}
            {order.shippingAddress.address2 ? `, ${order.shippingAddress.address2}` : ""},{" "}
            {order.shippingAddress.postcode} {order.shippingAddress.city}, {order.shippingAddress.state}
          </p>
        </section>
      )}

      {isUnpaid && order.kind === "STORE" && (
        <button onClick={cancelOrder} className="w-full py-2 text-center text-xs text-white/40 active:text-red-400">
          Cancel order
        </button>
      )}

      <BankInReceiptViewModal
        open={receiptPreviewIndex != null}
        receipts={uploadedReceipts}
        initialIndex={receiptPreviewIndex ?? 0}
        onClose={() => setReceiptPreviewIndex(null)}
      />
    </div>
  );
}
